import { useCallback, useMemo, useRef, useState } from "react";

import { Box, Typography, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import theme from "assets/theme";
import { useAppContextController } from "context/AppContext";
import moment from "moment";
import { useQueryClient } from "react-query";
import { calculateTotalHours } from "utils/helpers/payrollHelper";
import useArrowKeysFieldSelection from "utils/useArrowKeysFieldSelection";
import EditableCellNumber from "../../../../EditableCellNumber";
import EditableCellTime from "../../../../EditableCellTime";
import TimecardEditorTableActions from "../../TimecardEditorTableActions";

export const TimecardEditorPartnerMembersList = ({
  applicants = [],
  isLoading,
  eventDate,
  onChangeField,
  navigateWithBack,
  setSelectedRowForDeductions,
  isForPayrollEditor,
  editLocked,
  onSelectRowForCorrection,
  correctionActive,
  selectedApplicants,
  setSelectedApplicants,
}) => {
  const [modalInfo, setModalInfo] = useState({ data: {} });
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState(false);
  const { company, currentEvent, currentVenue } = useAppContextController();
  const queryClient = useQueryClient();

  // ------------ FIELD SWITCH ------------
  const inputRefs = useRef({});
  const getFinalFieldRowIndex = useCallback(
    (i, iSub) => {
      if (!applicants?.length) return 0;
      const newIndex = applicants.reduce((prev, current, ind) => {
        if (ind > i) return prev;

        if (ind === i) return prev;
        return prev + 1;
      }, 0);

      return newIndex;
    },
    [applicants]
  );
  const gridDimensions = useMemo(
    () => ({
      rows: getFinalFieldRowIndex(applicants?.length ?? 0),
      cols: isForPayrollEditor ? 3 : 4,
    }),
    [applicants?.length, getFinalFieldRowIndex, isForPayrollEditor]
  );
  const { getFieldKey, handleKeyDown: handleKeyDownField } = useArrowKeysFieldSelection({
    inputRefs,
    gridDimensions,
  });
  // --------------------------------------

  const matches = useMediaQuery(theme.breakpoints.up("lg"));

  const columns = useMemo(
    () => [
      {
        headerName: "First Name",
        field: "firstName",
        flex: 1,
      },
      {
        headerName: "Last Name",
        field: "lastName",
        flex: 1,
      },
      ...(!isForPayrollEditor
        ? [
            {
              headerName: "Time In",
              field: "timeIn",
              width: 150,
              sortable: false,
              renderCell: ({ value, row, api, id }) => {
                const index = api?.getRowIndex(id);
                const finalIndex = getFinalFieldRowIndex(index);

                if (editLocked || (correctionActive && !row?.rosterData?.isCorrection)) {
                  return (
                    <span>{`${
                      row?.rosterData?.timeOut
                        ? moment(row?.rosterData?.timeIn).format("hh:mma")
                        : ""
                    }`}</span>
                  );
                }

                return (
                  <EditableCellTime
                    eventDate={currentEvent?.eventDate}
                    time={row?.rosterData?.timeIn ?? ""}
                    setTime={(val) => onChangeField(row, "timeIn", val ? val.toISOString() : val)}
                    onKeyDown={(e) => handleKeyDownField(e, finalIndex, 0)}
                    ref={(el) => {
                      if (el) {
                        inputRefs.current[getFieldKey(finalIndex, 0)] = el;
                      }
                    }}
                  />
                );
              },
            },
            {
              headerName: "Time Out",
              field: "timeOut",
              width: 150,
              sortable: false,
              renderCell: ({ value, row, api, id }) => {
                const index = api?.getRowIndex(id);
                const finalIndex = getFinalFieldRowIndex(index);

                if (editLocked || (correctionActive && !row?.rosterData?.isCorrection)) {
                  return (
                    <span>{`${
                      row?.rosterData?.timeOut
                        ? moment(row?.rosterData?.timeOut).format("hh:mma")
                        : ""
                    }`}</span>
                  );
                }

                return (
                  <EditableCellTime
                    eventDate={currentEvent?.eventDate}
                    time={row?.rosterData?.timeOut ?? ""}
                    setTime={(val) => onChangeField(row, "timeOut", val ? val.toISOString() : val)}
                    onKeyDown={(e) => handleKeyDownField(e, finalIndex, 1)}
                    ref={(el) => {
                      if (el) {
                        inputRefs.current[getFieldKey(finalIndex, 1)] = el;
                      }
                    }}
                  />
                );
              },
            },
          ]
        : [
            // {
            //   headerName: "Salary",
            //   field: "salary",
            //   width: 80,
            //   sortable: false,
            //   renderCell: ({ value, row, api, id }) => {
            //     return (
            //       <Checkbox
            //         checked={row?.rosterData?.salary === "Yes"}
            //         onChange={(v) => onChangeField(row, "salary", v.target.checked ? "Yes" : "No")}
            //       />
            //     );
            //   },
            // },
          ]),
      {
        headerName: "Pay Rate",
        field: "payRate",
        width: 150,
        sortable: false,
        renderCell: ({ value, row, api, id }) => {
          const index = api?.getRowIndex(id);
          const finalIndex = getFinalFieldRowIndex(index);

          if (editLocked || (correctionActive && !row?.rosterData?.isCorrection)) {
            return <span>{`$${row?.rosterData?.payRate ?? "0"}`}</span>;
          }

          return (
            <EditableCellNumber
              amount={row?.rosterData?.payRate ?? ""}
              setAmount={(val) => onChangeField(row, "payRate", val ? parseFloat(val) : val)}
              onKeyDown={(e) => handleKeyDownField(e, finalIndex, isForPayrollEditor ? 0 : 2)}
              adornmentSymbol="$"
              ref={(el) => {
                if (el) {
                  inputRefs.current[getFieldKey(finalIndex, isForPayrollEditor ? 0 : 2)] = el;
                }
              }}
            />
          );
        },
      },
      {
        headerName: "Bill Rate",
        field: "billRate",
        width: 150,
        sortable: false,
        renderCell: ({ value, row, api, id }) => {
          const index = api?.getRowIndex(id);
          const finalIndex = getFinalFieldRowIndex(index);

          if (editLocked || (correctionActive && !row?.rosterData?.isCorrection)) {
            return <span>{`$${row?.rosterData?.billRate ?? "0"}`}</span>;
          }

          return (
            <EditableCellNumber
              amount={row?.rosterData?.billRate ?? ""}
              setAmount={(val) => onChangeField(row, "billRate", val ? parseFloat(val) : val)}
              onKeyDown={(e) => handleKeyDownField(e, finalIndex, isForPayrollEditor ? 1 : 3)}
              adornmentSymbol="$"
              ref={(el) => {
                if (el) {
                  inputRefs.current[getFieldKey(finalIndex, isForPayrollEditor ? 1 : 3)] = el;
                }
              }}
            />
          );
        },
      },
      {
        headerName: "Total Hrs",
        field: "totalHours",
        width: 120,
        sortable: false,
        renderCell: ({ value, row, api, id }) => {
          let hourDifference = 0;
          const hours = calculateTotalHours(row, currentEvent?.timeZone);
          if (correctionActive && row?.rosterData?.isCorrection) {
            // This row is corrected. Display difference of hours
            const originalHours = calculateTotalHours(
              {
                ...row,
                rosterData: {
                  ...row?.rosterData,
                  timeIn: row?.rosterData?.originalTimeIn,
                  timeOut: row?.rosterData?.originalTimeOut,
                },
              },
              currentEvent?.timeZone
            );
            hourDifference = hours - originalHours;
            hourDifference = Math.round(hourDifference * 100) / 100;
          }

          const hoursRounded = Math.round(hours * 100) / 100;
          const hourDifferenceSign = hourDifference >= 0 ? "+" : "-";

          return row?.slug
            ? ""
            : `${hoursRounded}${
                correctionActive && row?.rosterData?.isCorrection
                  ? ` (${hourDifferenceSign}${Math.abs(hourDifference)})`
                  : ""
              }`;
        },
      },
      {
        headerName: "Deduc.",
        field: "deductions",
        width: 90,
        sortable: false,
        renderCell: ({ value, row, api, id }) => {
          return (
            <Typography
              variant="text"
              sx={{
                color: `${
                  // eslint-disable-next-line no-nested-ternary
                  parseFloat(row?.rosterData?.deductionAmount ?? 0) > 0
                    ? "success"
                    : parseFloat(row?.rosterData?.deductionAmount ?? 0) < 0
                    ? "error"
                    : "dark"
                }.main`,
              }}
            >
              {`(${(row?.rosterData?.deductionAmount || 0) < 0 ? "-" : ""}${
                row?.rosterData?.deductionAmountType === "flat" ? "$" : ""
              }${Math.abs(row?.rosterData?.deductionAmount || 0)}${
                row?.rosterData?.deductionAmountType === "percentage" ? "%" : ""
              })`}
            </Typography>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        sortable: false,
        skipForSubtable: true,
        renderCell: ({ value, row, index }) => (
          <TimecardEditorTableActions
            id={row?._id}
            row={row}
            setNavigateUrl={navigateWithBack}
            isShiftJob={false}
            isExpanded={false}
            onToggleSubList={() => {}}
            onOpenPartnerMembers={() => {}}
            deductionsOnly
            onSelectRowForDeductions={setSelectedRowForDeductions}
            editLocked={editLocked}
            onSelectRowForCorrection={onSelectRowForCorrection}
            correctionActive={correctionActive}
          />
        ),
      },
    ],
    [
      correctionActive,
      currentEvent?.eventDate,
      currentEvent?.timeZone,
      editLocked,
      getFieldKey,
      getFinalFieldRowIndex,
      handleKeyDownField,
      isForPayrollEditor,
      navigateWithBack,
      onChangeField,
      onSelectRowForCorrection,
      setSelectedRowForDeductions,
    ]
  );

  return (
    <Box
      sx={{
        minHeight: "100px",
        display: "flex",
        flexGrow: 1,
        overflow: "scroll",
        width: "100%",
      }}
    >
      <DataGrid
        rows={applicants}
        columns={columns}
        // columnVisibilityModel={visibleColumns}
        checkboxSelection={!editLocked}
        selectionModel={selectedApplicants}
        onSelectionModelChange={setSelectedApplicants}
        disableSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        onCellKeyDown={(_, e) => {
          // e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
        getRowId={(row) => row?._id}
        loading={isLoading}
      />
    </Box>
  );
};
